import { unified } from "unified";
import remarkParse from "remark-parse";
import remarkGfm from "remark-gfm";
import remarkDirective from "remark-directive";
import remarkRehype from "remark-rehype";
import rehypeSanitize, { defaultSchema } from "rehype-sanitize";
import rehypeStringify from "rehype-stringify";
import { visit } from "unist-util-visit";
function allowedLink(value) {
    try {
        const url = new URL(value, "https://site.invalid");
        return (value.startsWith("/") && !value.startsWith("//")) || (url.protocol === "https:" && !url.username && !url.password);
    }
    catch {
        return false;
    }
}
const EMBED_PROVIDERS = {
    "youtube.com": "YouTube", "www.youtube.com": "YouTube", "youtu.be": "YouTube", "vimeo.com": "Vimeo", "www.vimeo.com": "Vimeo",
    "instagram.com": "Instagram", "www.instagram.com": "Instagram", "x.com": "X", "www.x.com": "X", "twitter.com": "X", "www.twitter.com": "X",
};
const clip = (value, max) => value.length <= max ? value : `${value.slice(0, max - 1).trimEnd()}…`;
function directivePlugin(options) {
    return (tree, file) => {
        const source = typeof file.value === "string" ? file.value : String(file.value ?? "");
        visit(tree, (raw) => {
            const node = raw;
            if (node.type === "html") {
                node.type = "text";
                return;
            }
            // The post title is the page's H1, so the body has H2 and H3 only.
            if (node.type === "heading")
                node.depth = !node.depth || node.depth <= 2 ? 2 : 3;
            if (node.type !== "leafDirective" && node.type !== "textDirective" && node.type !== "containerDirective")
                return;
            const attrs = node.attributes ?? {};
            const label = node.children?.map((child) => child.value ?? "").join("") ?? "";
            const text = (value) => { node.type = "text"; node.value = value; delete node.children; delete node.data; };
            // Only leaf (`::name`) directives are part of the format. Anything else the parser took for a directive
            // ("10:30", "Re:Zero", "mailto:me@x.com", ":smile:") is the owner's own text and stays exactly as written.
            const start = node.position?.start.offset;
            const end = node.position?.end.offset;
            const original = start !== undefined && end !== undefined ? source.slice(start, end) : `${node.type === "containerDirective" ? ":::" : ":"}${node.name ?? ""}`;
            if (node.type === "textDirective") {
                text(original);
                return;
            }
            if (node.type === "containerDirective") {
                node.type = "paragraph";
                node.children = [{ type: "text", value: original }];
                delete node.data;
                return;
            }
            if (node.name === "photo") {
                // A photo is the post's own asset; anything unknown (or an old record-id directive) renders nothing, caption included.
                const photo = attrs.asset && attrs.version && /^[1-9][0-9]{0,8}$/.test(attrs.version) ? options.media?.(attrs.asset, Number(attrs.version)) : undefined;
                if (!photo) {
                    text("");
                    node.missingPhoto = true;
                    return;
                }
                node.data = { hName: "figure", ...(attrs.width === "wide" ? { hProperties: { className: "stackje-photo-wide" } } : {}) };
                node.children = [{ type: "image", url: photo.src, alt: attrs.alt ?? photo.alt }];
                return;
            }
            if (node.name === "embed" || node.name === "bookmark" || node.name === "button") {
                const url = attrs.url;
                if (!url || !allowedLink(url)) {
                    text(label || "");
                    return;
                }
                const host = new URL(url, "https://site.invalid").hostname;
                const provider = node.name === "embed" ? EMBED_PROVIDERS[host] : undefined;
                const link = (className, children) => ({ type: "link", url, children, ...(className ? { data: { hProperties: { className: [className] } } } : {}) });
                node.type = "paragraph";
                delete node.data;
                if (provider) {
                    // A privacy-friendly facade: a plain link, so no third-party script or frame loads with the page.
                    node.children = [link("stackje-embed-facade", [{ type: "text", value: label || attrs.title || `${provider === "Instagram" || provider === "X" ? "View" : "Watch"} on ${provider}` }])];
                }
                else if (node.name === "button") {
                    node.children = [link(undefined, [{ type: "text", value: label || attrs.title || url }])];
                }
                else {
                    // A link preview card from what the directive holds (title, description); the host stands in when there is no title.
                    // An image would mean loading a third-party picture with the page, so none is shown.
                    const shown = host === "site.invalid" ? url : host.replace(/^www\./, "");
                    const part = (value, kind) => ({ type: "emphasis", data: { hName: kind }, children: [{ type: "text", value }] });
                    node.children = [link("stackje-bookmark", [
                            part(clip(attrs.title ?? (label || shown), 120), "strong"),
                            ...(attrs.description ? [part(clip(attrs.description, 200), "span")] : []),
                            part(shown, "span"),
                        ])];
                }
                return;
            }
            // An unknown leaf directive is text too, never silently dropped.
            node.type = "paragraph";
            node.children = [{ type: "text", value: original }];
            delete node.data;
        });
        const captions = (parent) => {
            const children = parent.children;
            if (!children)
                return;
            for (let index = 0; index < children.length; index += 1) {
                const child = children[index];
                const nextCaption = () => {
                    const next = children[index + 1];
                    return next?.type === "paragraph" && next.children?.length === 1 && next.children[0]?.type === "text" ? next.children[0].value?.match(/^:\s+(.+)$/s)?.[1] : undefined;
                };
                if (child.missingPhoto && nextCaption()) {
                    children.splice(index + 1, 1);
                    continue;
                }
                if (child.data?.hName === "figure") {
                    const caption = nextCaption();
                    if (caption) {
                        child.children?.push({ type: "paragraph", data: { hName: "figcaption" }, children: [{ type: "text", value: caption }] });
                        children.splice(index + 1, 1);
                    }
                }
                captions(child);
            }
        };
        captions(tree);
    };
}
/** The distinct `(asset, version)` pairs the body's photo directives name, in order of first use. Unknown or old-style directives are skipped. */
export function postPhotoRefs(bodyMd) {
    const tree = unified().use(remarkParse).use(remarkGfm).use(remarkDirective).parse(bodyMd);
    const found = new Map();
    visit(tree, (raw) => {
        const node = raw;
        if (node.type !== "leafDirective" || node.name !== "photo")
            return;
        const { asset, version } = node.attributes ?? {};
        if (!asset || !version || !/^[A-Za-z][A-Za-z0-9_-]{0,63}$/.test(asset) || !/^[1-9][0-9]{0,8}$/.test(version))
            return;
        found.set(`${asset}/${version}`, { assetId: asset, version: Number(version) });
    });
    return [...found.values()];
}
const PHOTO_LINE = /^::photo(\[[^\]\n]*\])?\{([^}\n]*)\}/;
const encodeAltAttr = (value) => value.replace(/&/g, "&amp;").replace(/"/g, "&quot;").replace(/\\/g, "&#92;").replace(/\}/g, "&#125;").replace(/\s+/g, " ").trim();
const decodeAltAttr = (value) => value.replace(/&#(\d+);/g, (_, code) => String.fromCodePoint(Number(code))).replace(/&quot;/g, '"').replace(/&amp;/g, "&");
/** The photo directives in a body that name this photo: line index and the text between the braces. */
function photoLines(lines, ref) {
    const found = [];
    lines.forEach((line, index) => {
        const inner = PHOTO_LINE.exec(line)?.[2];
        if (inner === undefined)
            return;
        const attrs = {};
        for (const match of inner.matchAll(/([\w-]+)=(?:"([^"]*)"|(\S+))/g))
            attrs[match[1]] = match[2] ?? match[3] ?? "";
        if (attrs.asset === ref.assetId && attrs.version === String(ref.version))
            found.push({ index, inner });
    });
    return found;
}
/** The description the first use of this photo in the body gives it, or null when the body doesn't use it. */
export function photoAltIn(bodyMd, ref) {
    const first = photoLines(bodyMd.split("\n"), ref)[0];
    if (!first)
        return null;
    const alt = /(?:^|\s)alt=(?:"([^"]*)"|(\S+))/.exec(first.inner);
    return alt ? decodeAltAttr(alt[1] ?? alt[2] ?? "") : "";
}
/**
 * The body with this photo's description changed on every directive that uses it. Only the `alt`
 * attribute of those directives changes; every other byte stays. Null when the body doesn't use the photo.
 */
export function withPhotoAlt(bodyMd, ref, alt) {
    const lines = bodyMd.split("\n");
    const uses = photoLines(lines, ref);
    if (!uses.length)
        return null;
    const encoded = `alt="${encodeAltAttr(alt)}"`;
    for (const { index, inner } of uses) {
        const next = /(?:^|\s)alt=(?:"[^"]*"|\S+)/.test(inner) ? inner.replace(/(^|\s)alt=(?:"[^"]*"|\S+)/, (_, lead) => `${lead}${encoded}`) : `${inner} ${encoded}`;
        lines[index] = lines[index].replace(`{${inner}}`, () => `{${next}}`);
    }
    return lines.join("\n");
}
/** Resolves a body's photo directives against the photos a revision has. Anything else renders nothing. */
export function revisionMedia(photos, mediaUrl) {
    const known = new Set((photos ?? []).map((photo) => `${photo.assetId}/${photo.version}`));
    return (assetId, version) => known.has(`${assetId}/${version}`) ? { src: mediaUrl({ assetId, version }), alt: "" } : undefined;
}
/** Shared safe renderer for published pages, previews and the writing view. */
export function renderPostMarkdown(bodyMd, options = {}) {
    return String(unified().use(remarkParse).use(remarkGfm).use(remarkDirective).use(directivePlugin, options)
        .use(remarkRehype).use(rehypeSanitize, { ...defaultSchema, tagNames: [...(defaultSchema.tagNames ?? []), "figure", "figcaption"], attributes: { ...defaultSchema.attributes, figure: ["className"], a: [...(defaultSchema.attributes?.a ?? []).filter((entry) => !Array.isArray(entry) || entry[0] !== "className"), ["className", "data-footnote-backref", "stackje-embed-facade", "stackje-bookmark"]] } }).use(rehypeStringify).processSync(bodyMd));
}
/**
 * The editor's Markdown escapes brackets, so `[add …]` would be saved as `\[add …\]`. Both render the
 * same; this keeps the plain form, so the stored post, Stax's suggestions and the editor all match.
 */
export function unescapePlaceholders(bodyMd) {
    return bodyMd.replace(/\\\[(add\s[^\]\n]{0,200}?)\\\]/gi, "[$1]");
}
/** Visible gaps left for the owner; warnings never block publishing. */
export function postPlaceholders(bodyMd) {
    return [...bodyMd.matchAll(/\[add\s+[^\]\n]{1,200}\]/gi)].map(match => ({ text: match[0], from: match.index, to: match.index + match[0].length }));
}
